import { useRef, useState } from 'react'
import { vendorUpdateSubtypes } from '@intelhub/shared/ontology'
import { Button } from '@/components/ui/button'
import { useRouteBenchmarkToVendor } from '@/hooks/useBenchmarkQueries'

/**
 * KAN-822 T9 (Captain Q61) — "Route to Vendor stories" for one benchmark
 * candidate, with the operator's optional Vendor update subtype.
 *
 * The picker's options are the canonical `vendor-update-subtype` ontology
 * entries (one authority: the same set the API validates and the stories CHECK
 * enforces), plus "Not set". One request id is minted per opened confirmation,
 * so a second click on Confirm is a replay the API answers with the same
 * receipt, never a second route.
 *
 * DARK: rendered only where the caller gates it (VITE_VENDOR_ROUTING_UI_ENABLED).
 */
const SUBTYPE_OPTIONS = [...vendorUpdateSubtypes].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))

export interface RouteToVendorActionProps {
  benchmark: { id: string; title: string; dispositionVersion?: number }
}

export function RouteToVendorAction({ benchmark }: RouteToVendorActionProps) {
  const route = useRouteBenchmarkToVendor(benchmark.id)
  const [requestId, setRequestId] = useState<string | null>(null)
  const [subtype, setSubtype] = useState('')
  const trigger = useRef<HTMLButtonElement>(null)
  const selectId = `route-subtype-${benchmark.id}`
  const canRoute = benchmark.dispositionVersion !== undefined && !route.isPending && !route.isSuccess

  const close = () => {
    setRequestId(null)
    setSubtype('')
    trigger.current?.focus()
  }

  const confirm = () => {
    if (!requestId || benchmark.dispositionVersion === undefined) return
    route.mutate({
      expectedVersion: benchmark.dispositionVersion,
      requestId,
      vendorUpdateSubtype: subtype === '' ? null : subtype,
    })
  }

  return (
    <span className="inline-flex flex-col items-end gap-1">
      {requestId === null ? (
        <Button
          ref={trigger}
          size="sm"
          variant="outline"
          disabled={!canRoute}
          onClick={() => setRequestId(crypto.randomUUID())}
        >
          {route.isPending ? 'Routing…' : route.isSuccess ? 'Routed' : 'Route to Vendor'}
        </Button>
      ) : (
        <span
          role="group"
          data-ph-no-capture
          aria-label={`Route “${benchmark.title}” to Vendor stories`}
          className="inline-flex flex-wrap items-center justify-end gap-2 rounded-md border p-2"
        >
          <label htmlFor={selectId} className="text-xs text-muted-foreground">
            Vendor update subtype
          </label>
          <select
            id={selectId}
            className="h-8 rounded-md border bg-background px-2 text-sm"
            value={subtype}
            onChange={(e) => setSubtype(e.target.value)}
          >
            <option value="">Not set</option>
            {SUBTYPE_OPTIONS.map((e) => (
              <option key={e.slug} value={e.slug}>
                {e.name}
              </option>
            ))}
          </select>
          <Button size="sm" variant="default" disabled={route.isPending} onClick={confirm}>
            Confirm route
          </Button>
          <Button size="sm" variant="ghost" onClick={close}>
            Cancel
          </Button>
        </span>
      )}
      <span role="status" aria-live="polite" className="text-xs text-muted-foreground">
        {route.isSuccess
          ? 'Routed to Vendor stories (queued)'
          : route.error
            ? `Could not route: ${route.error.message}`
            : ''}
      </span>
    </span>
  )
}
